"use client";

import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

export default function ServiceDetailsModal({
  modal_center,
  setmodal_center,
  tog_center,
  service,
  onStatusUpdate,
}) {
  const [isUpdating, setIsUpdating] = useState(false);

  const updateServiceStatus = async (newStatus) => {
    if (!service || !service.id) return;

    setIsUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/services/${service.id}/status`,
        { is_active: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const statusText = newStatus ? "activated" : "deactivated";
        toast.success(`Service ${statusText} successfully`, {
          position: "bottom-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
        });

        // Update the service in parent component
        if (onStatusUpdate) {
          onStatusUpdate(service.id, newStatus);
        }

        // Close modal
        tog_center();
      } else {
        toast.error(
          response.data.message || "Failed to update service status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating service status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge color="success">Active</Badge>
    ) : (
      <Badge color="danger">Inactive</Badge>
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper function to safely get numeric values
  const safeNumber = (value, defaultValue = 0) => {
    if (typeof value === "number") return value;
    if (typeof value === "string")
      return Number.parseFloat(value) || defaultValue;
    return defaultValue;
  };

  // Helper function to safely get string values
  const safeString = (value, defaultValue = "N/A") => {
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return value.toString();
    return defaultValue;
  };

  if (!service) return null;

  return (
    <Modal
      isOpen={modal_center}
      toggle={tog_center}
      centered
      size="lg"
      className="service-details-modal"
    >
      <ModalHeader toggle={tog_center}>
        <div className="d-flex align-items-center">
          <i className="ri-service-line me-2 text-primary"></i>
          Service Details
          <Badge color="light" className="ms-2">
            #{service.id}
          </Badge>
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="service-details">
          {/* Service Header */}
          <Row className="mb-4">
            <Col md={8}>
              <div className="d-flex align-items-start">
                {service.image_url && (
                  <img
                    src={service.image_url || "/placeholder.svg"}
                    alt={service.title || "Service"}
                    className="rounded me-3"
                    style={{
                      width: "100px",
                      height: "100px",
                      objectFit: "cover",
                    }}
                  />
                )}
                <div>
                  <h4 className="mb-2">{service.title || "Service"}</h4>
                  <p className="text-muted mb-2">
                    {service.description || "No description available"}
                  </p>
                  <div className="d-flex gap-2 align-items-center">
                    {getStatusBadge(service.is_active)}
                    {service.category && (
                      <Badge color="light" className="text-dark">
                        {service.category.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="service-price">
                <h3 className="text-success mb-0">
                  {formatPrice(service.price)}
                </h3>
                <small className="text-muted">Service Price</small>
              </div>
            </Col>
          </Row>

          {/* Service Details */}
          <Row className="mb-4">
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-information-line me-2"></i>
                  Service Information
                </h6>
                <div className="info-item mb-2">
                  <strong>Service ID:</strong> #{service.id}
                </div>
                <div className="info-item mb-2">
                  <strong>Title:</strong> {service.title || "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Category:</strong>{" "}
                  {service.category?.name || "No Category"}
                </div>
                <div className="info-item mb-2">
                  <strong>Price:</strong> {formatPrice(service.price)}
                </div>
                <div className="info-item mb-2">
                  <strong>Status:</strong> {getStatusBadge(service.is_active)}
                </div>
                <div className="info-item mb-2">
                  <strong>Created:</strong> {formatDateTime(service.created_at)}
                </div>
                <div className="info-item mb-2">
                  <strong>Last Updated:</strong>{" "}
                  {formatDateTime(service.updated_at)}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-user-line me-2"></i>
                  Service Provider
                </h6>
                <div className="info-item mb-2">
                  <strong>Provider Name:</strong>{" "}
                  {service.service_provider?.name ||
                    service.user?.name ||
                    "Unknown Provider"}
                </div>
                <div className="info-item mb-2">
                  <strong>Provider Email:</strong>{" "}
                  {service.service_provider?.email ||
                    service.user?.email ||
                    "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Provider Phone:</strong>{" "}
                  {service.service_provider?.phone ||
                    service.user?.phone ||
                    "N/A"}
                </div>
                <div className="info-item mb-2">
                  <strong>Provider Type:</strong>{" "}
                  {(service.service_provider?.account_type_id ||
                    service.user?.account_type_id) === 1
                    ? "Influencer"
                    : (service.service_provider?.account_type_id ||
                        service.user?.account_type_id) === 2
                    ? "Business"
                    : "User"}
                </div>
                {(service.service_provider?.created_at ||
                  service.user?.created_at) && (
                  <div className="info-item mb-2">
                    <strong>Provider Since:</strong>{" "}
                    {formatDate(
                      service.service_provider?.created_at ||
                        service.user?.created_at
                    )}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Service Description */}
          {service.description && (
            <Row className="mb-4">
              <Col>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-file-text-line me-2"></i>
                    Service Description
                  </h6>
                  <div className="description-content p-3 bg-light rounded">
                    <p className="mb-0">{service.description}</p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Category Details */}
          {service.category && (
            <Row className="mb-4">
              <Col md={6}>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-folder-line me-2"></i>
                    Category Details
                  </h6>
                  <div className="info-item mb-2">
                    <strong>Category Name:</strong> {service.category.name}
                  </div>
                  {service.category.description && (
                    <div className="info-item mb-2">
                      <strong>Category Description:</strong>{" "}
                      {service.category.description}
                    </div>
                  )}
                  {service.category.icon && (
                    <div className="info-item mb-2">
                      <strong>Category Icon:</strong>{" "}
                      <i className={service.category.icon}></i>
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <div className="info-section">
                  <h6 className="text-primary mb-3">
                    <i className="ri-bar-chart-line me-2"></i>
                    Service Statistics
                  </h6>
                  <div className="info-item mb-2">
                    <strong>Service Orders:</strong>{" "}
                    {safeNumber(service.orders_count)}
                  </div>
                  <div className="info-item mb-2">
                    <strong>Total Revenue:</strong>{" "}
                    {formatPrice(
                      safeNumber(
                        service.total_revenue ||
                          service.statistics?.total_revenue
                      )
                    )}
                  </div>
                  <div className="info-item mb-2">
                    <strong>Average Rating:</strong>{" "}
                    {safeString(service.average_rating, "No ratings yet")}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Service Timeline */}
          <Row>
            <Col>
              <div className="info-section">
                <h6 className="text-primary mb-3">
                  <i className="ri-time-line me-2"></i>
                  Service Timeline
                </h6>
                <div className="timeline">
                  <div className="timeline-item d-flex justify-content-between align-items-center p-2 border-bottom">
                    <span>Service Created</span>
                    <small className="text-muted">
                      {formatDateTime(service.created_at)}
                    </small>
                  </div>
                  {service.updated_at !== service.created_at && (
                    <div className="timeline-item d-flex justify-content-between align-items-center p-2 border-bottom">
                      <span>Last Updated</span>
                      <small className="text-muted">
                        {formatDateTime(service.updated_at)}
                      </small>
                    </div>
                  )}
                  <div className="timeline-item d-flex justify-content-between align-items-center p-2">
                    <span>Current Status</span>
                    <small className="text-muted">
                      {service.is_active ? "Active" : "Inactive"}
                    </small>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="d-flex justify-content-between w-100">
          <div className="status-actions">
            <h6 className="mb-2">Update Status:</h6>
            <div className="d-flex gap-2">
              {!service.is_active && (
                <Button
                  color="success"
                  size="sm"
                  onClick={() => updateServiceStatus(1)}
                  disabled={isUpdating}
                >
                  <i className="ri-check-line me-1"></i>
                  Activate
                </Button>
              )}
              {service.is_active && (
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => updateServiceStatus(0)}
                  disabled={isUpdating}
                >
                  <i className="ri-close-line me-1"></i>
                  Deactivate
                </Button>
              )}
            </div>
          </div>
          <div>
            <Button color="secondary" onClick={tog_center}>
              Close
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
